import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import CloseIcon from '@mui/icons-material/Close';
import DatasetLinkedOutlinedIcon from '@mui/icons-material/DatasetLinkedOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QueueOutlinedIcon from '@mui/icons-material/QueueOutlined';
import { Box, Button, DialogContentText, Paper, Typography } from '@mui/material';

import { TooltipIconButton, usePromise } from '@paragon/client-tools';
import { SharedComponentSet } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUser, hasFreeSubscription, isWithinFreeTrialPeriod } from '@paragon/login-tools';
import { OptionalTooltip } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';
import { Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';
import { Blurb, useWalkthroughContext } from '@paragon/tour-guide';

import { showChooseProjectDialog } from '../../store/actions/popupActions';
import { copySharedComponentSetToMyOrganization } from '../../store/actions/sharedComponentSetActions';
import { rightToolBarShowSet } from '../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../hooks';

function useWalkthroughBlurbs() {
  const dispatch = useAppDispatch();

  const onFreeTier = useAppSelector(hasFreeSubscription);

  return useMemo(
    (): Blurb[] => [
      {
        anchor: 'context-toolbar',

        title: 'Welcome to Paragon!',

        renderContent: () => (
          <DialogContentText>
            <p>
              This is your current project. Here, you can change settings for the entire project or swap projects
              entirely.
            </p>

            {onFreeTier && (
              <p>
                As a Free User, you are given access to sample projects. The ability to create additional projects
                requires an upgrade to a paid plan. Contact us for more information regarding access to paid plans.
              </p>
            )}
          </DialogContentText>
        ),
      },

      {
        anchor: 'left-sidebar',
        title: 'The Left Sidebar',
        renderContent: () => (
          <DialogContentText>
            This is where all component designs in the project are displayed. Click any component to select and view it
            in the scene or right-click for additional context menus. Additionally, if the selected project has a
            layout, that view mode can be toggled at the top of the sidebar.
          </DialogContentText>
        ),
      },

      {
        anchor: 'scene-canvas',

        title: 'The Scene',
        renderContent: () => (
          <DialogContentText>
            <p>
              Here you will find the active component design or layout. You can pan the camera with your left mouse
              button and zoom with the scroll wheel.
            </p>

            <p>
              Click on items such as members and plate pairs to select them and right-click to access context menus
              specific to the selected items.
            </p>
          </DialogContentText>
        ),
      },

      {
        anchor: 'standard-toolbar',

        title: 'The Toolbar',
        renderContent: () => (
          <DialogContentText>
            Toolbars are displayed at the top of the scene and contain the most relevant actions such as Create,
            Analyze, and Download. As items are selected in the scene, additional buttons may appear dynamically.
          </DialogContentText>
        ),
      },

      {
        anchor: 'right-sidebar',

        title: 'The Right Sidebar',
        beforeShow: (proceed) => {
          dispatch(rightToolBarShowSet(true));
          setTimeout(proceed, 150);
        },
        renderContent: () => (
          <DialogContentText>
            The right sidebar allows you to explore the contents of the active component design as well as view or edit
            the properties of the currently selected item in the scene.
          </DialogContentText>
        ),
      },

      {
        anchor: 'right-sidebar-control-button',
        title: 'Accessing the Right Sidebar',
        renderContent: () => (
          <DialogContentText>
            The right sidebar is usually hidden and can be toggled with this button.
          </DialogContentText>
        ),
      },

      // {
      //   anchor: 'user-menu',

      //   beforeShow: (proceed) => {
      //     dispatch(rightToolBarShowSet(false));
      //     setTimeout(proceed, 150);
      //   },

      //   title: 'The User Menu',
      //   renderContent: () => (
      //     <DialogContentText>
      //       Links to a feedback form and additional support documentation can be found in the user menu.
      //     </DialogContentText>
      //   ),
      // },

      {
        anchor: null,
        beforeShow: (proceed) => {
          dispatch(rightToolBarShowSet(false));
          setTimeout(proceed, 150);
        },
        title: 'Start Designing!',

        renderContent: () => (
          <DialogContentText>
            <p>
              We hope you enjoy Paragon. Please let us know if you have any questions or if we can help you in any way.
            </p>
            <p>-The Paragon Team</p>
          </DialogContentText>
        ),
      },
    ],
    [dispatch, onFreeTier],
  );
}

function useSampleComponentSets() {
  const [isLoading, setIsLoading] = useState(true);
  const [didError, setDidError] = useState(false);

  const [samples, setSamples] = useState([] as readonly SharedComponentSet[]);

  useEffect(() => {
    designService.sharedComponentSets
      .fetchSamples()
      .then(setSamples)
      .catch(() => setDidError(true))
      .finally(() => setIsLoading(false));
  }, []);

  return [isLoading, didError, samples] as const;
}

export function WelcomeDialog() {
  const dispatch = useAppDispatch();

  const [temporarilyHidden, setTemporarilyHidden] = useState(false);

  const blurbs = useWalkthroughBlurbs();
  const walkthrough = useWalkthroughContext();

  const currentUserIsOnFreeTier = useAppSelector(hasFreeSubscription);
  const loggedInUser = useAppSelector(getLoggedInUser);
  const currentUserIsOnFreeTrial = isWithinFreeTrialPeriod(loggedInUser);

  const [samplesLoading, samplesFailedToLoad, samples] = useSampleComponentSets();

  const [sampleProjectCopied, setSampleProjectCopied] = useState(false);

  const hasSamples = !samplesLoading && !samplesFailedToLoad && samples.length > 0;

  const projectsQuery = usePromise(designService.projects.fetchSummaries);
  const seededProject = projectsQuery.isSuccess && projectsQuery.data.length === 1 ? projectsQuery.data[0] : null;

  function chooseProjectInstead() {
    dispatch(showChooseProjectDialog({ isStartup: true }));
  }

  useEffect(() => {
    function onFinishedWalkthrough() {
      setTemporarilyHidden(false);
    }

    walkthrough.events.addEventListener('finish', onFinishedWalkthrough);
    return () => void walkthrough.events.removeEventListener('finish', onFinishedWalkthrough);
  });

  if (!currentUserIsOnFreeTier && !currentUserIsOnFreeTrial) return null;

  return (
    <Dialog
      open={!temporarilyHidden}
      variant="design"
      onClose={chooseProjectInstead}
      contentSx={createScrollbarStyling}
      slotProps={{
        paper: {
          sx: (theme) => ({
            [theme.breakpoints.up('lg')]: {
              maxWidth: 'none!important',
            },
          }),
        },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', sm: 'contents' },
        }}
      >
        <TooltipIconButton enableInteractive title="Close" sx={{ float: 'right' }} onClick={chooseProjectInstead}>
          <CloseIcon />
        </TooltipIconButton>
      </Box>

      <Typography variant="h4" sx={{ marginTop: 2, marginBottom: 4, textAlign: 'center' }}>
        Welcome to Paragon!
      </Typography>
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: 3,

          [theme.breakpoints.down('lg')]: {
            flexDirection: 'column',
          },

          [theme.breakpoints.up('lg')]: {
            flexDirection: 'row',
            alignItems: 'stretch',
          },
        })}
      >
        <Paper
          sx={(theme) => ({
            overflow: 'hidden',

            [theme.breakpoints.only('xs')]: {
              // screen width - (dialog margin * 2) - (content padding * 2)
              width: 'calc(100vw - 112px)',

              // the above calculated width * 9/16 aspect ratio
              height: 'calc((100vw - 112px) * 9 / 16)',
            },

            minWidth: { sm: '426px', md: '640px' },
            height: { sm: '240px', md: '360px', lg: 'unset' },

            borderRadius: '4px',
            transform: 'translateZ(0px)',
          })}
        >
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/GfnXmiZOxXk?si=ebQtes-foaxU1sYY"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </Paper>

        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',

            gap: 3,

            [theme.breakpoints.between('sm', 'lg')]: { flexDirection: 'row', '& > *': { flexGrow: 1 } },
          })}
        >
          <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, paddingTop: 1 }}>
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Learn
            </Typography>

            <Button
              size="large"
              variant="contained"
              sx={{ whiteSpace: 'nowrap' }}
              href="https://docs.paragontruss.com"
              target="_blank"
              startIcon={<MenuBookIcon />}
            >
              View Documentation
            </Button>

            <Button
              size="large"
              variant="outlined"
              sx={{ whiteSpace: 'nowrap' }}
              startIcon={<ArrowCircleUpIcon sx={{ rotate: '90deg' }} />}
              onClick={() => {
                setTemporarilyHidden(true);

                setTimeout(() => {
                  walkthrough.queueBlurbs(blurbs);
                }, 0);
              }}
            >
              Start Interactive Tour
            </Button>
          </Paper>

          <Paper sx={{ display: 'flex', padding: 2, paddingTop: 1, flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Get Started
            </Typography>

            {seededProject == null ? (
              <InlineNotification type="error">
                Please contact Paragon Customer Support to ensure your account is set up correctly.
              </InlineNotification>
            ) : (
              <Button
                fullWidth
                variant="contained"
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<AddIcon />}
                disabled={seededProject == null}
                onClick={() => {
                  if (seededProject) window.location.href = `/${seededProject.guid}`;
                }}
              >
                Create Blank Project
              </Button>
            )}

            <OptionalTooltip
              visible
              placement="right"
              title={!hasSamples ? 'No sample projects available' : 'Open a sample project (with layout) in a new tab'}
            >
              <Button
                fullWidth
                variant="outlined"
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<DatasetLinkedOutlinedIcon />}
                href={`/${samples[0]?.lookupCode}`}
                target="_blank"
                disabled={!hasSamples}
              >
                View Sample Project
              </Button>
            </OptionalTooltip>

            <OptionalTooltip
              visible
              placement="right"
              title={
                !hasSamples
                  ? 'No sample projects available'
                  : sampleProjectCopied
                    ? 'Sample project opened in new tab'
                    : 'Create your own copy of the sample project to edit'
              }
            >
              <Button
                fullWidth
                variant="outlined"
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<QueueOutlinedIcon />}
                onClick={() => {
                  setSampleProjectCopied(true);
                  dispatch(copySharedComponentSetToMyOrganization(samples[0]?.lookupCode));
                }}
                disabled={!hasSamples || sampleProjectCopied}
              >
                Copy Sample Project
              </Button>
            </OptionalTooltip>
          </Paper>
        </Box>
      </Box>
    </Dialog>
  );
}
